import { AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/atoms/studio/ui/button";
import type { DestructiveAction } from "@/services/testing";

/**
 * Confirmation gate shown before a RUN that would DELETE records or EDIT an
 * existing record. Safe plans (create/verify → empty `destructive_actions`) never
 * reach this — they run with zero friction. Each action's server-authored `label`
 * is shown verbatim so the user sees exactly what the run will do.
 */
export function DestructiveRunDialog({
	actions,
	planName,
	onConfirm,
	onCancel,
}: {
	actions: DestructiveAction[];
	planName?: string | null;
	onConfirm: () => void;
	onCancel: () => void;
}) {
	if (actions.length === 0) return null;
	const deletes = actions.filter((a) => a.kind === "delete").length;
	const edits = actions.length - deletes;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in"
			role="dialog"
			aria-modal="true"
			aria-labelledby="destructive-run-title"
			onClick={onCancel}
		>
			<div
				className="w-full max-w-md overflow-hidden rounded-lg border border-destructive/40 bg-card shadow-xl animate-in zoom-in-95"
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header — red/warning accent */}
				<div className="flex items-start gap-3 border-b border-destructive/30 bg-destructive/10 px-4 py-3">
					<span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive-strong">
						<AlertTriangle className="h-4 w-4" />
					</span>
					<div className="min-w-0">
						<div id="destructive-run-title" className="text-sm font-semibold text-foreground">
							This run changes real data
						</div>
						<div className="text-[11px] text-muted-foreground">
							{planName ? <span className="font-medium">{planName}</span> : "This test"} will make{" "}
							{deletes > 0 && (
								<span className="font-medium text-destructive-strong">
									{deletes} deletion{deletes === 1 ? "" : "s"}
								</span>
							)}
							{deletes > 0 && edits > 0 && " and "}
							{edits > 0 && (
								<span className="font-medium text-warning-strong">
									{edits} edit{edits === 1 ? "" : "s"} to existing records
								</span>
							)}
							. This cannot be undone automatically.
						</div>
					</div>
				</div>

				{/* Action list — each label verbatim */}
				<ul className="max-h-56 divide-y divide-border overflow-auto px-4 py-2">
					{actions.map((a, i) => (
						<li key={i} className="flex items-start gap-2 py-1.5 text-[12px] text-foreground">
							{a.kind === "delete" ? (
								<Trash2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive-strong" />
							) : (
								<Pencil className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-strong" />
							)}
							<span className="min-w-0 break-words">{a.label}</span>
						</li>
					))}
				</ul>

				{/* Actions */}
				<div className="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-4 py-3">
					<Button size="sm" variant="ghost" onClick={onCancel}>
						Cancel
					</Button>
					<Button size="sm" variant="destructive" onClick={onConfirm}>
						Run anyway
					</Button>
				</div>
			</div>
		</div>
	);
}
